import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Trash2, LogOut, BookOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { api } from "@/services/api";
import toast from "react-hot-toast";

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error("Password is required");
      return;
    }

    setIsDeleting(true);
    try {
      await api.delete("/auth/account", { data: { password: deletePassword } });
      toast.success("Account deleted successfully");
      logout();
      nav("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLogout = () => {
    logout();
    nav("/login");
  };

  return (
    <div>
      <div className="mb-10">
        <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-2">Settings</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter">Account Settings.</h1>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Theme Toggle */}
        <div className="border border-slate-200 dark:border-slate-800 rounded-sm bg-white dark:bg-[#121214] p-6">
          <h2 className="text-lg font-semibold mb-4">Appearance</h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Theme</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                {theme === "dark" ? "Dark mode" : "Light mode"}
              </div>
            </div>
            <button
              onClick={toggle}
              className="p-3 border border-slate-300 dark:border-slate-700 hover:border-slate-900 dark:hover:border-white rounded-sm"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Account Info */}
        <div className="border border-slate-200 dark:border-slate-800 rounded-sm bg-white dark:bg-[#121214] p-6">
          <h2 className="text-lg font-semibold mb-4">Account Information</h2>
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Email</div>
              <div className="text-sm">{user?.email || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Account Created</div>
              <div className="text-sm">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</div>
            </div>
          </div>
        </div>

        {/* Documentation */}
        <div className="border border-slate-200 dark:border-slate-800 rounded-sm bg-white dark:bg-[#121214] p-6">
          <h2 className="text-lg font-semibold mb-4">Documentation</h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Getting Started Guide</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">Learn how to create your first invoice</div>
            </div>
            <button
              onClick={() => nav("/app/getting-started")}
              className="px-4 py-2 border border-slate-300 dark:border-slate-700 hover:border-slate-900 dark:hover:border-white rounded-sm text-sm"
            >
              <BookOpen className="w-4 h-4 inline mr-2" />
              View Guide
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="border border-red-200 dark:border-red-900/40 rounded-sm bg-white dark:bg-[#121214] p-6">
          <h2 className="text-lg font-semibold mb-4 text-red-600 dark:text-red-400">Danger Zone</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Logout</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Sign out of your account</div>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 border border-slate-300 dark:border-slate-700 hover:border-red-600 hover:text-red-600 rounded-sm text-sm"
              >
                <LogOut className="w-4 h-4 inline mr-2" />
                Logout
              </button>
            </div>
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-red-600 dark:text-red-400">Delete Account</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Permanently delete your account and all data</div>
                </div>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 text-sm"
                >
                  <Trash2 className="w-4 h-4 inline mr-2" />
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-[#121214] rounded-sm w-full max-w-md p-6"
            >
              <h2 className="text-xl font-extrabold tracking-tighter mb-4">Delete Account</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                This action cannot be undone. Deleting your account will permanently remove:
              </p>
              <ul className="text-sm text-slate-600 dark:text-slate-400 mb-4 list-disc list-inside space-y-1">
                <li>All organizations</li>
                <li>All invoices and invoice items</li>
                <li>All customers</li>
                <li>All invoice templates</li>
                <li>All inventory data</li>
              </ul>
              <p className="text-xs text-slate-500 dark:text-slate-500 mb-4">
                Note: Template versions will be retained in the database for audit purposes.
              </p>
              <div className="mb-4">
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Enter your password to confirm
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Password"
                  className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm"
                />
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletePassword("");
                  }}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-sm text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 text-sm"
                >
                  {isDeleting ? "Deleting..." : "Delete Account"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
